import { api, writeDB } from './store';
import { VenueSection } from './venue';
import { apollo } from './graphql';
import {
  EVENT_PRINTED_TICKETS,
  PRINT_TICKETS,
  RETURN_TICKETS,
  UPDATE_PERSONALIZATION,
  POLL_TICKETS,
} from './queries/event';
import { groupBy, compact } from 'lodash';
import { parseDate, wait, indexBy, comparePotentialNumbers, compareNullity } from '../util/util';
import { EventPrintedTicketsPollingQueryQuery } from '../generated/graphql';

export interface Ticket {
  id: string;
  eventId: string;
  personalIdentifier: string | null;
  seat: Seat | null;
  validatedBy: string | null;
  validationCode: string;
  usedAt: Date | null;
}

export interface Seat {
  id?: string;
  name?: string;
  row?: string;
  venueSection?: Partial<VenueSection> | null;
  venueSectionId?: string;
}

export interface PrintedTicket {
  id: string;
  validationCode: string;
  comment: string | null;
  user: {
    name?: string | null;
    email: string;
  } | null;
  pricingCategoryName: string;
  orderedAt: Date;
  total: number;
  personalIdentifier: string | null;
  invalidated: boolean;
  productVariantDescription: string;
  url: string | null;
  venueSectionId?: string | null;
  seat?: Seat | null;
  order?: {
    user?: {
      email: string;
    } | null;
    tags?: string[] | null;
  } | null;
}

export interface PrintedTicketGroup {
  id: string;
  orderedAt: Date;
  total: number;
  amount: number;
  pricingCategoryName: string;
  productVariantDescription: string;
  userName: string | null;
  userEmail: string | null;
  comment: string | null;
  tickets: PrintedTicket[];
  urls: string[];
  customerEmail: string | null;
  tags: string[] | null;
}

export async function getTicketPreview({
  eventId,
  productVariantId,
  venueSectionId,
}: {
  eventId: string;
  productVariantId?: string;
  venueSectionId?: string;
}): Promise<{
  preview: string;
}> {
  const response = await api.showTicketPreview({ eventId, productVariantId, venueSectionId });
  return response.data;
}

function parsePrintedTickets(eventId: string, data?: EventPrintedTicketsPollingQueryQuery): PrintedTicket[] {
  const printedTickets = data?.event?.printedTickets?.map((e) => ({ ...e, orderedAt: parseDate(e.orderedAt)! })) ?? [];

  if (printedTickets) {
    const existing = writeDB.table('events').find(eventId)?.printedTickets ?? [];
    const newList = Object.values({ ...indexBy(existing, 'id'), ...indexBy(printedTickets, 'id') });
    writeDB.table('events').update(eventId, { printedTickets: newList });
  }
  return printedTickets ?? [];
}

export async function printSeatedTickets(variables: {
  seatIds: string[];
  productVariantId: string;
  comment: string;
  email?: string;
  tags?: string[];
  personalIdentifier?: string;
}): Promise<void> {
  await apollo.mutate({
    mutation: PRINT_TICKETS,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    variables: variables as any,
  });
}

export async function printGeneralAdmissionTickets(variables: {
  sectionId: string;
  productVariantId: string;
  amount: number;
  pool?: string;
  comment: string;
  email?: string;
  tags?: string[];
  name?: string;
  lastName?: string;
  personalIdentifier?: string;
}): Promise<void> {
  await apollo.mutate({
    mutation: PRINT_TICKETS,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    variables: variables as any,
  });
}

export function groupTickets(tickets: PrintedTicket[]): PrintedTicketGroup[] {
  if (!tickets.length) return [];

  return Object.values(
    groupBy(tickets, (ticket) =>
      [ticket.orderedAt.getTime(), ticket.pricingCategoryName, ticket.productVariantDescription, ticket.comment].join(
        '-'
      )
    )
  ).map((unsortedGroup) => {
    const group = sortTicketsPerSeat(unsortedGroup);
    const ticket = group[0];
    return {
      id: ticket.id,
      orderedAt: ticket.orderedAt,
      total: ticket.total,
      amount: group.length,
      pricingCategoryName: ticket.pricingCategoryName,
      productVariantDescription: ticket.productVariantDescription,
      userName: ticket.user?.name ?? null,
      userEmail: ticket.user?.email ?? null,
      comment: ticket.comment,
      urls: compact(group.filter((e) => !e.invalidated).map((e) => e.url)),
      tickets: group,
      customerEmail: ticket?.order?.user?.email ?? null,
      tags: ticket?.order?.tags ?? null,
    };
  });
}

function sortTicketsPerSeat(tickets: PrintedTicket[]): PrintedTicket[] {
  return tickets.sort((a, b) => {
    const seatComparison = compareSeats(a.seat, b.seat);
    return seatComparison !== 0 ? seatComparison : a.validationCode.localeCompare(b.validationCode);
  });
}

export async function returnTickets(variables: { itemIds: string[]; blockReason?: string }): Promise<void> {
  await apollo.mutate({
    mutation: RETURN_TICKETS,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    variables: variables as any,
  });
}

export async function updatePersonalization(variables: { itemId: string; personalIdentifier: string }): Promise<void> {
  await apollo.mutate({
    mutation: UPDATE_PERSONALIZATION,
    variables,
  });
}

interface PollState {
  eventId: string;
  ticketIds: Set<string>;
}

async function pollTickets(state: PollState): Promise<void> {
  // Step 1: Clean up state
  const tickets = (writeDB.table('events').find(state.eventId)?.printedTickets ?? []).filter((e) =>
    state.ticketIds.has(e.id)
  );
  tickets.forEach((ticket) => {
    if (ticket.url || ticket.invalidated) {
      state.ticketIds.delete(ticket.id);
    }
  });
  if (state.ticketIds.size === 0) return;

  // Step 2: query for current state of tickets
  const result = await apollo.query({
    query: POLL_TICKETS,
    variables: { eventId: state.eventId, ticketIds: Array.from(state.ticketIds) },
    fetchPolicy: 'network-only',
  });
  parsePrintedTickets(state.eventId, result.data);

  // Step 3: Wait and start the next round
  await wait(5);
  await pollTickets(state);
}

export async function loadPrintedTickets({ id }: { id: string }): Promise<void> {
  const result = await apollo.query({
    query: EVENT_PRINTED_TICKETS,
    variables: { id },
    fetchPolicy: 'network-only',
  });
  const pendingTickets = parsePrintedTickets(id, result.data).filter((e) => e.url === null);
  if (pendingTickets.length > 0) {
    pollTickets({ eventId: id, ticketIds: new Set(pendingTickets.map((e) => e.id)) });
  }
}

export function compareSeats(a: Seat | null | undefined, b: Seat | null | undefined): number {
  // Unseated goes before seated.
  const nullComparison = compareNullity(a, b);
  if (nullComparison !== 0) return nullComparison;

  // Compare by section name.
  const venueSectionNameComparison = comparePotentialNumbers(a?.venueSection?.name, b?.venueSection?.name);
  if (venueSectionNameComparison !== 0) return venueSectionNameComparison;

  // Compare by row.
  const rowComparison = comparePotentialNumbers(a?.row, b?.row);
  if (rowComparison !== 0) return rowComparison;

  // Compare by seat name.
  const nameComparison = comparePotentialNumbers(a?.name, b?.name);
  return nameComparison;
}
